import { Component, inject, model } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { PasswordModule } from 'primeng/password';
import { senhasIguais } from '../../pages/auth/redefinir-senha/redefinir-senha.component';
import { LoginService } from '../../services/login.service';
import { SENHA_MAXIMA, SENHA_MINIMA, SenhaService } from '../../services/senha.service';
import { ToastService } from '../../services/toast.service';

/** Troca da própria senha pelo usuário logado, aberta pela barra lateral. */
@Component({
    selector: 'app-alterar-senha',
    standalone: true,
    imports: [ReactiveFormsModule, ButtonModule, DialogModule, MessageModule, PasswordModule],
    template: `
        <p-dialog header="Alterar senha" [(visible)]="visivel" [modal]="true" [draggable]="false" [resizable]="false" [style]="{ width: 'min(420px, 96vw)' }" (onHide)="limpar()">
            <form [formGroup]="form" (ngSubmit)="salvar()" class="flex flex-col gap-5" novalidate>
                <input type="email" autocomplete="username" [value]="login.session()?.email ?? ''" class="hidden" aria-hidden="true" tabindex="-1" readonly />
                <div class="flex flex-col gap-2">
                    <label for="senha-atual" class="text-sm font-medium">Senha atual</label>
                    <p-password inputId="senha-atual" formControlName="senhaAtual" autocomplete="current-password" [toggleMask]="true" [fluid]="true" [feedback]="false" />
                    @if (invalido('senhaAtual')) { <small class="text-red-500">Informe a senha atual.</small> }
                </div>
                <div class="flex flex-col gap-2">
                    <label for="nova-senha-propria" class="text-sm font-medium">Nova senha</label>
                    <p-password inputId="nova-senha-propria" formControlName="novaSenha" autocomplete="new-password" [toggleMask]="true" [fluid]="true" [feedback]="false" />
                    @if (invalido('novaSenha')) { <small class="text-red-500">Use de {{ minimo }} a 72 caracteres.</small> }
                    @else { <small class="text-muted-color">Pelo menos {{ minimo }} caracteres.</small> }
                </div>
                <div class="flex flex-col gap-2">
                    <label for="confirmacao-propria" class="text-sm font-medium">Confirme a nova senha</label>
                    <p-password inputId="confirmacao-propria" formControlName="confirmacao" autocomplete="new-password" [toggleMask]="true" [fluid]="true" [feedback]="false" />
                    @if (form.hasError('senhasDiferentes') && form.get('confirmacao')!.touched) { <small class="text-red-500">As senhas não coincidem.</small> }
                </div>
                @if (erro) { <p-message severity="error" [text]="erro" /> }
                <div class="flex justify-end gap-2">
                    <p-button label="Cancelar" type="button" severity="secondary" [text]="true" (onClick)="visivel.set(false)" />
                    <p-button label="Alterar senha" type="submit" [loading]="salvando" />
                </div>
            </form>
        </p-dialog>
    `
})
export class AppAlterarSenha {
    private fb = inject(FormBuilder);
    private senhas = inject(SenhaService);
    private toast = inject(ToastService);
    readonly login = inject(LoginService);

    readonly visivel = model(false);
    readonly minimo = SENHA_MINIMA;
    salvando = false;
    erro = '';

    readonly form = this.fb.nonNullable.group({
        senhaAtual: ['', Validators.required],
        novaSenha: ['', [Validators.required, Validators.minLength(SENHA_MINIMA), Validators.maxLength(SENHA_MAXIMA)]],
        confirmacao: ['', Validators.required]
    }, { validators: senhasIguais });

    invalido(campo: 'senhaAtual' | 'novaSenha'): boolean {
        const controle = this.form.get(campo)!;
        return controle.invalid && controle.touched;
    }

    salvar(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) return;
        this.salvando = true;
        this.erro = '';
        const { senhaAtual, novaSenha } = this.form.getRawValue();
        this.senhas.alterar(senhaAtual, novaSenha).subscribe({
            next: () => {
                this.salvando = false;
                this.visivel.set(false);
                this.toast.sucesso('Senha alterada', 'Use a nova senha no próximo acesso.');
            },
            error: e => {
                this.salvando = false;
                this.erro = e?.error?.message || 'Não foi possível alterar a senha.';
            }
        });
    }

    limpar(): void {
        this.form.reset();
        this.erro = '';
    }
}
